import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';

interface CategoryRecord {
  BankTransactionCategoryId: number;
  Name: string;
  ParentId?: number | null;
}

interface CategorySelectorProps {
  label: string;
  value: string | number | null; // selected category id
  onChange: (id: number | '') => void;
  required?: boolean;
  disabled?: boolean;
  allowNoneOption?: boolean; // when true, allow clearing (e.g., "Leave unchanged")
  investorId?: number;
}

export default function CategorySelector(props: CategorySelectorProps) {
  const {
    label,
    value,
    onChange,
    required,
    disabled,
    allowNoneOption,
    investorId,
  } = props;
  const { t } = useLocalization();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const LIST_PANEL_WIDTH = 280;

  const authLoaded = useAuthIsLoaded();

  const { data: categoriesData, isFetching } = useGetCashflowCategoriesQuery(
    authLoaded ? { InvestorId: investorId } : skipToken,
  );

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Desktop submenu handling
  // Legacy anchor from floating submenu approach (removed)
  const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);
  const [pinnedParentId, setPinnedParentId] = useState<number | null>(null);

  // Mobile drill-down state
  const [drillParentId, setDrillParentId] = useState<number | null>(null);

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
    setHoveredParentId(null);
    setDrillParentId(null);
  };

  const {
    parentCategories,
    subcategoriesByParent,
    idToName,
  }: {
    parentCategories: CategoryRecord[];
    subcategoriesByParent: Record<number, CategoryRecord[]>;
    idToName: Record<number, string>;
  } = useMemo(() => {
    const parents: CategoryRecord[] = [];
    const subs: Record<number, CategoryRecord[]> = {};
    const idName: Record<number, string> = {};

    const all: CategoryRecord[] = categoriesData?.categories || [];
    all.forEach((c) => {
      idName[c.BankTransactionCategoryId] = c.Name;
    });
    // parents first
    all.forEach((c) => {
      if (!c.ParentId) {
        parents.push({ ...c, ParentId: null });
        subs[c.BankTransactionCategoryId] = [];
      }
    });
    // children
    all.forEach((c) => {
      if (c.ParentId && subs[c.ParentId]) {
        subs[c.ParentId].push(c);
      }
    });
    parents.sort((a, b) => a.Name.localeCompare(b.Name));
    Object.values(subs).forEach((arr) =>
      arr.sort((a, b) => a.Name.localeCompare(b.Name)),
    );
    return {
      parentCategories: parents,
      subcategoriesByParent: subs,
      idToName: idName,
    };
  }, [categoriesData]);

  const selectedLabel = useMemo(() => {
    if (value === null || value === '') return '';
    const idNum = typeof value === 'string' ? parseInt(value) : value;
    return idToName[idNum] || '';
  }, [value, idToName]);

  const handleSelect = useCallback(
    (id: number | '') => {
      onChange(id);
      handleClose();
    },
    [onChange],
  );

  const renderParentListItem = (parent: CategoryRecord) => (
    <ListItem
      key={parent.BankTransactionCategoryId}
      disablePadding
      onMouseEnter={() => {
        if (!isMobile && pinnedParentId === null) {
          setHoveredParentId(parent.BankTransactionCategoryId);
        }
      }}
      onMouseLeave={() => {
        if (!isMobile && pinnedParentId === null) {
          setHoveredParentId(null);
        }
      }}
    >
      <ListItemButton
        onClick={() => {
          if (isMobile) {
            setDrillParentId(parent.BankTransactionCategoryId);
          } else {
            setPinnedParentId((prev) =>
              prev === parent.BankTransactionCategoryId
                ? null
                : parent.BankTransactionCategoryId,
            );
            setHoveredParentId(parent.BankTransactionCategoryId);
          }
        }}
      >
        <ListItemText
          primary={parent.Name}
          slotProps={{
            primary: {
              fontWeight: 600,
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  // Note: floating submenu was replaced by a two-column panel that shares one hover zone

  // Two-column panel for desktop: left parents, right subcategories
  const renderSubcategoryPanel = () => {
    if (isMobile) return null;
    const activeParentId = pinnedParentId ?? hoveredParentId;
    if (activeParentId === null) return null;
    const subcats = subcategoriesByParent[activeParentId] || [];
    return (
      <List sx={{ minWidth: 260, maxHeight: 420, overflowY: 'auto', py: 0 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              handleSelect(activeParentId);
            }}
          >
            <ListItemText
              primary={idToName[activeParentId]}
              slotProps={{
                primary: {
                  fontWeight: 600,
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        {subcats.map((s) => (
          <ListItem
            key={s.BankTransactionCategoryId}
            disablePadding
          >
            <ListItemButton
              onClick={() => {
                handleSelect(s.BankTransactionCategoryId);
              }}
            >
              <ListItemText primary={s.Name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderDesktopMenu = () => (
    <Menu
      open={isOpen && !isMobile && !disabled}
      anchorEl={anchorRef.current}
      onClose={() => {
        setPinnedParentId(null);
        handleClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableAutoFocusItem
      slotProps={{
        list: {
          sx: {
            p: 0,
          },
        },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'stretch' }}
        onMouseLeave={() => {
          if (pinnedParentId === null) {
            setHoveredParentId(null);
          }
        }}
      >
        <List
          sx={{
            width: LIST_PANEL_WIDTH,
            maxHeight: 420,
            overflowY: 'auto',
            borderRight: 1,
            borderColor: 'divider',
            py: 0,
          }}
        >
          {allowNoneOption && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleSelect('');
                }}
              >
                <ListItemText
                  primary={t('leaveUnchanged', {
                    defaultValue: 'Leave unchanged',
                  })}
                />
              </ListItemButton>
            </ListItem>
          )}
          {parentCategories.map((parent) => (
            <ListItem
              key={parent.BankTransactionCategoryId}
              disablePadding
            >
              <ListItemButton
                onMouseEnter={() => {
                  if (pinnedParentId === null) {
                    setHoveredParentId(parent.BankTransactionCategoryId);
                  }
                }}
                onClick={() => {
                  setPinnedParentId((prev) =>
                    prev === parent.BankTransactionCategoryId
                      ? null
                      : parent.BankTransactionCategoryId,
                  );
                  setHoveredParentId(parent.BankTransactionCategoryId);
                }}
                selected={
                  (pinnedParentId ?? hoveredParentId) ===
                  parent.BankTransactionCategoryId
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <ListItemText
                  primary={parent.Name}
                  slotProps={{
                    primary: {
                      fontWeight: 600,
                    },
                  }}
                />
                {(subcategoriesByParent[parent.BankTransactionCategoryId]
                  ?.length || 0) > 0 && <ChevronRightIcon fontSize='small' />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {/* Only render subcategory panel when a parent is hovered or pinned and it has children */}
        {(pinnedParentId ?? hoveredParentId) !== null &&
          (subcategoriesByParent[(pinnedParentId ?? hoveredParentId) as number]
            ?.length || 0) > 0 &&
          renderSubcategoryPanel()}
      </Box>
    </Menu>
  );

  const renderMobileDialog = () => (
    <Dialog
      fullScreen
      open={isOpen && isMobile}
      onClose={handleClose}
    >
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
        >
          {drillParentId !== null && (
            <Button
              onClick={() => {
                setDrillParentId(null);
              }}
              startIcon={<ArrowBackIcon />}
            >
              {t('back')}
            </Button>
          )}
          <Typography
            variant='h6'
            sx={{ ml: drillParentId !== null ? 1 : 0 }}
          >
            {t('addTransactionModal.selectCategory')}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {allowNoneOption && drillParentId === null && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleSelect('');
              }}
            >
              <ListItemText
                primary={t('leaveUnchanged', {
                  defaultValue: 'Leave unchanged',
                })}
              />
            </ListItemButton>
          </ListItem>
        )}
        {drillParentId === null ? (
          <List>{parentCategories.map((p) => renderParentListItem(p))}</List>
        ) : (
          <List>
            {/* show option to choose the parent itself */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleSelect(drillParentId);
                }}
              >
                <ListItemText
                  primary={idToName[drillParentId]}
                  slotProps={{
                    primary: {
                      fontWeight: 600,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            {(subcategoriesByParent[drillParentId] || []).map((s) => (
              <ListItem
                key={s.BankTransactionCategoryId}
                disablePadding
              >
                <ListItemButton
                  onClick={() => {
                    handleSelect(s.BankTransactionCategoryId);
                  }}
                >
                  <ListItemText primary={s.Name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={disabled}
        >
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <FormControl
      fullWidth
      disabled={disabled}
    >
      <Box ref={anchorRef}>
        <TextField
          fullWidth
          label={label}
          required={required}
          value={selectedLabel}
          placeholder={
            isFetching ? t('loading') : t('addTransactionModal.selectCategory')
          }
          onClick={disabled ? undefined : handleOpen}
          disabled={disabled}
          slotProps={{ input: { readOnly: true } }}
        />
      </Box>
      {!disabled && renderDesktopMenu()}
      {!disabled && renderMobileDialog()}
    </FormControl>
  );
}
